/**
 * @file ErrorRecovery.jsx
 * @description Akıllı hata kurtarma bileşeni
 *
 * Hata durumlarında kullanıcıya alternatif öneriler sunar.
 * Mevcut sorguyu analiz ederek uygun öneriler gösterir.
 *
 * @module widget/components/ErrorRecovery
 */

import { AlertCircle, RefreshCw, Filter, MapPin, X } from "lucide-react";

/**
 * Hata tipine göre öneri oluşturur
 *
 * @param {Object} query - Mevcut arama sorgusu
 * @returns {Array<{id: string, label: string, action: string}>} Öneriler
 */
function getSuggestions(query) {
  const suggestions = [];
  const hasFilters = query?.filters && query.filters.length > 0;
  const path = query?.path || "";
  const pathParts = path.split("/");
  const locationPart = pathParts[1] || "";
  const locationSegments = locationPart.split("-");
  const hasDistrict = locationSegments.length > 1;
  const hasNeighborhood = locationSegments.length > 2;

  // Her zaman "Tekrar Dene" öner
  suggestions.push({
    id: "retry",
    label: "Tekrar Dene",
    action: "retry",
    icon: RefreshCw,
  });

  // Filtre varsa "Filtreleri Kaldır" öner
  if (hasFilters) {
    suggestions.push({
      id: "remove-filters",
      label: "Filtreleri Kaldır",
      action: "remove-filters",
      icon: Filter,
    });
  }

  // Mahalle varsa "İlçeye genişlet" öner
  if (hasNeighborhood) {
    suggestions.push({
      id: "expand-district",
      label: "İlçeye Genişlet",
      action: "expand-district",
      icon: MapPin,
    });
  }
  // İlçe varsa "İle genişlet" öner
  else if (hasDistrict) {
    suggestions.push({
      id: "expand-city",
      label: "İle Genişlet",
      action: "expand-city",
      icon: MapPin,
    });
  }

  return suggestions;
}

/**
 * Akıllı Hata Kurtarma Bileşeni
 *
 * @component
 * @param {Object} props
 * @param {string} props.error - Hata mesajı
 * @param {Object} props.query - Mevcut arama sorgusu
 * @param {boolean} props.isDark - Karanlık mod
 * @param {Function} props.onDismiss - Hatayı kapat
 * @param {Function} props.onRetry - Aramayı tekrarla
 * @param {Function} props.onRemoveFilters - Filtreleri kaldırarak ara
 * @param {Function} props.onExpandLocation - Konumu genişleterek ara
 */
export function ErrorRecovery({
  error,
  query,
  isDark,
  onDismiss,
  onRetry,
  onRemoveFilters,
  onExpandLocation,
}) {
  const suggestions = getSuggestions(query);

  const handleAction = (action) => {
    switch (action) {
      case "retry":
        onRetry?.();
        break;
      case "remove-filters":
        onRemoveFilters?.();
        break;
      case "expand-district":
      case "expand-city":
        onExpandLocation?.(action);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`mx-4 mt-3 p-4 rounded-xl border ${
        isDark
          ? "bg-red-500/10 border-red-500/20"
          : "bg-red-50 border-red-200"
      }`}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <AlertCircle
            className={`h-5 w-5 flex-shrink-0 mt-0.5 ${
              isDark ? "text-red-400" : "text-red-600"
            }`}
            strokeWidth={1.5}
          />
          <div>
            <h4
              className={`text-sm font-semibold ${
                isDark ? "text-red-300" : "text-red-800"
              }`}
            >
              Arama Başarısız
            </h4>
            <p
              className={`text-sm mt-1 ${
                isDark ? "text-red-200/70" : "text-red-700"
              }`}
            >
              {error}
            </p>
          </div>
        </div>
        <button
          onClick={onDismiss}
          className={`p-1 rounded-md transition-colors ${
            isDark
              ? "hover:bg-white/10 text-white/50"
              : "hover:bg-black/5 text-black/50"
          }`}
          aria-label="Hatayı kapat"
        >
          <X className="h-4 w-4" strokeWidth={2} />
        </button>
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="mt-4">
          <p
            className={`text-xs font-medium mb-2 ${
              isDark ? "text-white/40" : "text-black/40"
            }`}
          >
            Şunları deneyebilirsiniz:
          </p>
          <div className="flex flex-wrap gap-2">
            {suggestions.map((suggestion) => {
              const Icon = suggestion.icon;
              return (
                <button
                  key={suggestion.id}
                  onClick={() => handleAction(suggestion.action)}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    isDark
                      ? "bg-white/10 text-white/80 hover:bg-white/20"
                      : "bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                  }`}
                >
                  <Icon className="h-3.5 w-3.5" strokeWidth={1.5} />
                  {suggestion.label}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

export default ErrorRecovery;
